/**
 * CounterpartiesTab — Groups expanded_transactions by counterparty entity/address.
 *
 * Reads from Zustand stores (useSelectionStore, useGraphStore) — new tab pattern.
 * Canvas operations are delegated via callback props since they still live in Workbench.
 *
 * Key features:
 *  - Entity grouping: arkhamEntity → entity_name → address-level rows
 *  - Sort: volume (default), tx count, risk, recent, name
 *  - Eye toggle: shows whether counterparty is on canvas
 *  - Expand chevron: preview 3 most recent txs inline
 *  - "View all" link: switches to Transfers tab filtered by counterparty
 */

import React, { useMemo, useState, useCallback } from 'react';
import {
    Eye, EyeOff, ChevronDown, ChevronRight,
    ArrowDownLeft, ArrowUpRight, Copy, Search, SortAsc, SortDesc, ShieldCheck
} from 'lucide-react';
import { useSelectionStore } from '../../stores/useSelectionStore';
import { useGraphStore } from '../../stores/useGraphStore';
import { useStashStore } from '../../stores/useStashStore';
import { useFilterStore } from '../../stores/useFilterStore';
import { useTransactionStore } from '../../stores/useTransactionStore';
import { canvasController } from '../../services/canvasController';
import { formatCurrency, DUST_USD_THRESHOLD } from '../../utils/edgeConsolidation';
import { isHiddenDustRow } from '../../utils/stampedRows';
import type { TransactionEvidence } from '../../types/graph';
import { TxLoadBar } from './TxLoadBar';
import type { EnrichmentStatus } from './TxLoadBar';
import { safeStr } from '../../utils/safeStr';
import { EntityNameWithCopy } from './EntityNameWithCopy';

// ── Types ────────────────────────────────────────────────────────────

interface CounterpartyGroup {
    /** Group key: entity name or address */
    key: string;
    /** Display name (entity name or truncated address) */
    displayName: string;
    /** Entity type if known */
    entityType: string | null;
    /** All addresses in this group */
    addresses: string[];
    /** Primary address (first address — used for hover cross-highlight) */
    primaryAddress: string;
    /** All transactions with this counterparty */
    transactions: TransactionEvidence[];
    /** Aggregated stats */
    totalVolume: number;
    txCount: number;
    inboundVolume: number;
    outboundVolume: number;
    /** Most recent tx timestamp */
    lastSeen: number;
    /** Risk score (max across txs) */
    riskScore: number;
    /** Whether any address in this group is on canvas */
    isOnCanvas: boolean;
    /** Whether the canvas element is hidden (_visibility === 0) */
    isHiddenOnCanvas: boolean;
    /** Whether any transaction in this group is part of a trace path */
    isOnTracePath: boolean;
    /** Whether any transaction in this group is verified by forensic records */
    hasForensicLinks: boolean;
    /** Trace context: hop info from the gold-path edge */
    traceContext: { hop: number; totalHops: number; targetName: string; direction: string } | null;
    /** Primary token symbol */
    primaryToken: string | null;
}

type SortField = 'volume' | 'txCount' | 'risk' | 'recent' | 'name';
type SortDirection = 'asc' | 'desc';

export interface CounterpartiesTabProps {
    /** Add a counterparty node to canvas (address, entityName?, entityType?) */
    onAddCounterpartyToCanvas?: (address: string, entityName?: string, entityType?: string) => void;
    /** Remove a node from canvas by address */
    onRemoveFromCanvas?: (address: string) => void;
    /** Load transactions by count (same as Transfers tab) */
    onLoadByCount?: (count: number) => Promise<void>;
    /** Load transactions by date range */
    onLoadByDateRange?: (start: string, end: string, onProgress: (loaded: number, batch: number) => void) => Promise<void>;
    cancelDateRangeRef?: React.MutableRefObject<boolean>;
    txStartDate?: string;
    txEndDate?: string;
    setTxStartDate?: (v: string) => void;
    setTxEndDate?: (v: string) => void;
    dateRangeAllLoaded?: boolean;
    enrichmentStatus?: EnrichmentStatus;
}

// ── Helpers ──────────────────────────────────────────────────────────

const RISK_TYPE_SCORES: Record<string, number> = {
    sanctioned: 1.0, mixer: 0.9, scam: 0.85, ransomware: 0.85,
    gambling: 0.6, p2p: 0.4, bridge: 0.3, defi: 0.25,
    exchange: 0.1, unknown: 0.05,
};

function riskScoreForType(entityType: string | null | undefined): number {
    if (!entityType) return 0;
    return RISK_TYPE_SCORES[entityType.toLowerCase()] ?? 0.05;
}

function truncateAddr(addr: string): string {
    if (!addr || addr.length < 12) return addr || '';
    return `${addr.substring(0, 6)}...${addr.substring(addr.length - 4)}`;
}

function riskDotColor(entityType: string | null): string {
    if (!entityType) return 'bg-gray-400';
    const t = entityType.toLowerCase();
    if (['sanctioned', 'mixer', 'scam', 'ransomware'].includes(t)) return 'bg-red-500';
    if (['gambling', 'p2p', 'bridge'].includes(t)) return 'bg-yellow-500';
    if (['exchange'].includes(t)) return 'bg-green-500';
    if (['defi'].includes(t)) return 'bg-blue-500';
    return 'bg-gray-400';
}

function formatDateRange(txs: TransactionEvidence[]): string {
    const timestamps = txs
        .map(t => {
            const ts = t.timestamp;
            if (!ts) return NaN;
            const n = Number(ts);
            if (!isNaN(n)) return n > 1e12 ? n : n * 1000; // epoch s vs ms
            return new Date(ts as string).getTime();
        })
        .filter(t => !isNaN(t));
    if (timestamps.length === 0) return '';
    const min = new Date(Math.min(...timestamps));
    const max = new Date(Math.max(...timestamps));
    const fmt = (d: Date) => d.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });
    if (fmt(min) === fmt(max)) return fmt(min);
    return `${fmt(min)} – ${fmt(max)}`;
}

// ── Main Component ───────────────────────────────────────────────────

export const CounterpartiesTab: React.FC<CounterpartiesTabProps> = ({
    onAddCounterpartyToCanvas,
    onRemoveFromCanvas,
    onLoadByCount,
    onLoadByDateRange,
    cancelDateRangeRef,
    txStartDate,
    txEndDate,
    setTxStartDate,
    setTxEndDate,
    dateRangeAllLoaded,
    enrichmentStatus,
}) => {
    const selectedNode = useSelectionStore(s => s.selectedNode);
    const setActiveTab = useSelectionStore(s => s.setActiveTab);
    // A1: read the global transaction store (single source of truth) so this tab
    // shows the SAME set as the Transfers tab. Store-first with the same
    // precedence TransfersTab uses; falls back to the per-node copy when empty.
    const storeTxs = useTransactionStore(s => s.getNodeTransactions(selectedNode?.id || ''));
    const elements = useGraphStore(s => s.elements);
    // Subscribe to currencyMode so this tab re-renders when the inspector
    // header toggle flips ORIG ↔ USD. formatCurrency() reads the mode at
    // call time; without this subscription the row totals could stale until
    // an unrelated re-render kicked in.
    useFilterStore(s => s.currencyMode);

    const [sortField, setSortField] = useState<SortField>('volume');
    const [sortDir, setSortDir] = useState<SortDirection>('desc');
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedKey, setExpandedKey] = useState<string | null>(null);

    // Hover cross-highlight: read from store for canvas→inspector highlighting
    const hoveredNodeId = useSelectionStore(s => s.hoveredNodeId);
    const hoveredSource = useSelectionStore(s => s.hoveredSource);
    const setHoveredNode = useSelectionStore(s => s.setHoveredNode);

    // Derive canvas node addresses from elements (normalized for case-insensitive EVM match)
    const canvasNodeAddresses = useMemo(() => {
        const addrs = new Set<string>();
        elements.forEach(el => {
            if (!el.data?.source && el.data?.id) {
                addrs.add(el.data.id);
                if (el.data.id.startsWith('0x') || el.data.id.startsWith('0X')) {
                    addrs.add(el.data.id.toLowerCase());
                }
            }
        });
        return addrs;
    }, [elements]);

    // v5.3: "hidden" counterparties are stashed nodes (either via the eye
    // icon here, right-click Stash, or Inspector Stash). Read from the stash
    // store so the icon reflects any stash reason, not just our own.
    const stashedEntries = useStashStore(s => s.entries);
    const hiddenCanvasAddresses = useMemo(() => {
        const addrs = new Set<string>();
        for (const key in stashedEntries) {
            const entry = stashedEntries[key];
            if (entry.group !== 'nodes') continue;
            const id = entry.element?.data?.id;
            if (!id) continue;
            addrs.add(id);
            if (id.startsWith('0x') || id.startsWith('0X')) {
                addrs.add(id.toLowerCase());
            }
        }
        return addrs;
    }, [stashedEntries]);

    // Trace path matching: addresses directly connected via gold-path edge from selectedNode
    const traceAddresses = useMemo(() => {
        const addrs = new Set<string>();
        if (!selectedNode?.id) return addrs;
        for (const el of elements) {
            if (el.data?.edgeType === 'gold-path') {
                if (el.data.source === selectedNode.id && el.data.target) addrs.add(el.data.target);
                if (el.data.target === selectedNode.id && el.data.source) addrs.add(el.data.source);
            }
        }
        return addrs;
    }, [elements, selectedNode?.id]);

    // Trace path matching: collect tx_hashes from gold-path canvas edges connected to this node
    const traceTxHashes = useMemo(() => {
        const hashes = new Set<string>();
        if (!selectedNode?.id) return hashes;
        for (const el of elements) {
            if (el.data?.edgeType === 'gold-path' &&
                (el.data.source === selectedNode.id || el.data.target === selectedNode.id)) {
                if (el.data.tx_hash) hashes.add(el.data.tx_hash);
                // Consolidated edges: collect all tx hashes from trace_transactions
                if (Array.isArray(el.data.trace_transactions)) {
                    for (const t of el.data.trace_transactions) {
                        if (t.tx_hash) hashes.add(t.tx_hash);
                    }
                }
            }
        }
        return hashes;
    }, [elements, selectedNode?.id]);

    // Trace context lookup: counterparty address → {hop, totalHops, targetName, direction}
    const traceContextMap = useMemo(() => {
        const map = new Map<string, { hop: number; totalHops: number; targetName: string; direction: string }>();
        if (!selectedNode?.id) return map;
        for (const el of elements) {
            if (el.data?.edgeType === 'gold-path' &&
                (el.data.source === selectedNode.id || el.data.target === selectedNode.id) &&
                el.data.trace_hop) {
                const cpAddr = el.data.source === selectedNode.id ? el.data.target : el.data.source;
                if (!cpAddr) continue;
                map.set(cpAddr, {
                    hop: el.data.trace_hop,
                    totalHops: el.data.trace_total_hops,
                    targetName: el.data.trace_target_name || 'Exchange',
                    direction: el.data.trace_direction || 'auto',
                });
            }
        }
        return map;
    }, [elements, selectedNode?.id]);

    // ── Build counterparty groups ──
    const groups = useMemo((): CounterpartyGroup[] => {
        const nodeId = selectedNode?.id;
        if (!nodeId) return [];
        // Store-first (A1) — same source + precedence as TransfersTab, so both tabs agree.
        const txs: TransactionEvidence[] = storeTxs.length > 0 ? storeTxs : (selectedNode?.expanded_transactions || []);
        if (!txs.length) return [];

        // Group by entity or address
        const groupMap = new Map<string, {
            entityType: string | null;
            addresses: Set<string>;
            txs: TransactionEvidence[];
        }>();

        for (const tx of txs) {
            // Determine counterparty address
            const counterpartyAddr: string = tx.counterparty
                || (tx.from_address === nodeId ? tx.to_address : tx.from_address)
                || '';
            if (!counterpartyAddr || counterpartyAddr === nodeId) continue;

            // Grouping cascade: counterparty_entity → address
            const groupKey = tx.counterparty_entity || counterpartyAddr;

            let group = groupMap.get(groupKey);
            if (!group) {
                group = {
                    entityType: tx.counterparty_type || null,
                    addresses: new Set(),
                    txs: [],
                };
                groupMap.set(groupKey, group);
            }
            group.addresses.add(counterpartyAddr);
            group.txs.push(tx);
            // Update entity type if we get a more specific one
            if (tx.counterparty_type && !group.entityType) {
                group.entityType = tx.counterparty_type;
            }
        }

        // Convert to CounterpartyGroup[]
        return Array.from(groupMap.entries()).map(([key, g]) => {
            let totalVolume = 0;
            let inboundVolume = 0;
            let outboundVolume = 0;
            let maxRisk = riskScoreForType(g.entityType);
            let lastSeen = 0;
            const tokenCounts = new Map<string, number>();

            for (const tx of g.txs) {
                const val = Number(tx.value) || 0;
                totalVolume += val;
                if (tx.direction === 'incoming') inboundVolume += val;
                else outboundVolume += val;

                if (tx.counterparty_risk != null && tx.counterparty_risk > maxRisk) {
                    maxRisk = tx.counterparty_risk;
                }

                const ts = tx.timestamp;
                if (ts) {
                    const n = Number(ts);
                    const epoch = !isNaN(n) ? (n > 1e12 ? n : n * 1000) : new Date(ts as string).getTime();
                    if (!isNaN(epoch) && epoch > lastSeen) lastSeen = epoch;
                }

                const token = safeStr(tx.token_symbol) || 'native';
                tokenCounts.set(token, (tokenCounts.get(token) || 0) + val);
            }

            // Primary token = highest volume
            let primaryToken: string | null = null;
            let maxTokenVol = 0;
            tokenCounts.forEach((vol, tok) => {
                if (vol > maxTokenVol) { maxTokenVol = vol; primaryToken = tok; }
            });

            const isEntity = key !== g.txs[0]?.counterparty && key !== [...g.addresses][0];
            const displayName = isEntity ? key : truncateAddr(key);

            return {
                key,
                displayName,
                entityType: g.entityType,
                addresses: [...g.addresses],
                primaryAddress: [...g.addresses][0] || '',
                transactions: g.txs,
                totalVolume,
                txCount: g.txs.length,
                inboundVolume,
                outboundVolume,
                lastSeen,
                riskScore: maxRisk,
                isOnCanvas: [...g.addresses].some(a =>
                    canvasNodeAddresses.has(a) ||
                    ((a.startsWith('0x') || a.startsWith('0X')) && canvasNodeAddresses.has(a.toLowerCase()))
                ),
                isHiddenOnCanvas: [...g.addresses].some(a =>
                    hiddenCanvasAddresses.has(a) ||
                    ((a.startsWith('0x') || a.startsWith('0X')) && hiddenCanvasAddresses.has(a.toLowerCase()))
                ),
                isOnTracePath: [...g.addresses].some(a => traceAddresses.has(a)) ||
                    (traceTxHashes.size > 0 && g.txs.some(tx => tx.tx_hash && traceTxHashes.has(tx.tx_hash))),
                hasForensicLinks: g.txs.some((tx: any) => tx.forensic_context?.verified),
                traceContext: [...g.addresses].reduce<{ hop: number; totalHops: number; targetName: string; direction: string } | null>(
                    (acc, addr) => acc || traceContextMap.get(addr) || null, null
                ),
                primaryToken,
            };
        });
    }, [storeTxs, selectedNode?.id, selectedNode?.expanded_transactions, canvasNodeAddresses, hiddenCanvasAddresses, traceAddresses, traceTxHashes, traceContextMap]);

    // ── Filter ──
    const filtered = useMemo(() => {
        if (!searchQuery.trim()) return groups;
        const q = searchQuery.toLowerCase();
        return groups.filter(g =>
            g.displayName.toLowerCase().includes(q) ||
            g.key.toLowerCase().includes(q) ||
            g.addresses.some(a => a.toLowerCase().includes(q)) ||
            (g.entityType && g.entityType.toLowerCase().includes(q))
        );
    }, [groups, searchQuery]);

    // ── Sort ──
    const sorted = useMemo(() => {
        const arr = [...filtered];
        const dir = sortDir === 'asc' ? 1 : -1;
        arr.sort((a, b) => {
            switch (sortField) {
                case 'volume': return dir * (a.totalVolume - b.totalVolume);
                case 'txCount': return dir * (a.txCount - b.txCount);
                case 'risk': return dir * (a.riskScore - b.riskScore);
                case 'recent': return dir * (a.lastSeen - b.lastSeen);
                case 'name': {
                    const aName = a.displayName.toLowerCase();
                    const bName = b.displayName.toLowerCase();
                    // Push unknowns (addresses) to bottom
                    const aIsAddr = a.displayName.startsWith('0x') || a.displayName.includes('...');
                    const bIsAddr = b.displayName.startsWith('0x') || b.displayName.includes('...');
                    if (aIsAddr && !bIsAddr) return 1;
                    if (!aIsAddr && bIsAddr) return -1;
                    return dir * aName.localeCompare(bName);
                }
                default: return 0;
            }
        });
        return arr;
    }, [filtered, sortField, sortDir]);

    // ── Handlers ──
    const handleSort = useCallback((field: SortField) => {
        if (field === sortField) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDir(field === 'name' ? 'asc' : 'desc');
        }
    }, [sortField]);

    const handleToggleCanvas = useCallback((group: CounterpartyGroup) => {
        const primaryAddr = group.addresses[0];
        if (!primaryAddr) return;

        if (group.isOnCanvas && !group.isHiddenOnCanvas) {
            // Visible on canvas → stash it. Orphan edges go along for the ride.
            onRemoveFromCanvas?.(primaryAddr);
        } else if (group.isHiddenOnCanvas) {
            // Stashed → restore via the controller (all stash reasons handled).
            canvasController.restore([primaryAddr]);
        } else {
            // Not on canvas → add it
            onAddCounterpartyToCanvas?.(primaryAddr, group.displayName, group.entityType || undefined);
        }
    }, [onAddCounterpartyToCanvas, onRemoveFromCanvas]);

    const handleViewAllTxs = useCallback((group: CounterpartyGroup) => {
        // Set the filter so TransfersTab shows only this counterparty's txs
        useSelectionStore.getState().setCounterpartyFilter({
            address: group.key,
            displayName: group.displayName,
        });
        setActiveTab('transfers');
    }, [setActiveTab]);

    const copyToClipboard = useCallback((text: string) => {
        navigator.clipboard.writeText(text);
    }, []);

    // ── Stats for TxLoadBar ── (store-first, A1 — consistent with the groups above)
    const txs = storeTxs.length > 0 ? storeTxs : (selectedNode?.expanded_transactions || []);
    const incomingCount = useMemo(() => txs.filter(t => t.direction === 'incoming').length, [txs]);
    // M of "N of M": the real count from a load, else the triage count. Never the page size (NS-36).
    const approxTotal = selectedNode?.tx_total_real ?? selectedNode?.tx_count ?? 0;
    const coverageProps = {
        hasMore: selectedNode?.tx_has_more,
        truncated: selectedNode?.tx_truncated,
        incomplete: selectedNode?.tx_incomplete,
    };

    // ── Empty state ──
    if (!selectedNode) {
        return (
            <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">
                Select a node to view counterparties
            </div>
        );
    }

    if (groups.length === 0) {
        return (
            <div className="flex flex-col gap-2">
                {onLoadByCount && (
                    <div className="bg-secondary/30 rounded border border-border">
                        <TxLoadBar
                            txCount={0}
                            incomingCount={0}
                            outgoingCount={0}
                            canvasCount={0}
                            approxTotal={approxTotal}
                            {...coverageProps}
                            dateRangeAllLoaded={dateRangeAllLoaded}
                            onLoadByCount={onLoadByCount}
                            onLoadByDateRange={onLoadByDateRange}
                            cancelDateRangeRef={cancelDateRangeRef}
                            txStartDate={txStartDate}
                            txEndDate={txEndDate}
                            setTxStartDate={setTxStartDate}
                            setTxEndDate={setTxEndDate}
                            enrichmentStatus={enrichmentStatus}
                        />
                    </div>
                )}
                <div className="flex flex-col items-center justify-center h-32 text-muted-foreground text-sm gap-2">
                    <span>No transactions loaded</span>
                    <span className="text-xs">Load transactions above or run auto-investigate</span>
                </div>
            </div>
        );
    }

    const SortIcon = sortDir === 'asc' ? SortAsc : SortDesc;

    return (
        <div className="flex flex-col gap-1">
            {/* ── Row 1: Stats + Load ── */}
            {onLoadByCount && (
                <div className="bg-secondary/30 rounded border border-border">
                    <TxLoadBar
                        txCount={txs.length}
                        incomingCount={incomingCount}
                        outgoingCount={txs.length - incomingCount}
                        canvasCount={0}
                        approxTotal={approxTotal}
                        {...coverageProps}
                        dateRangeAllLoaded={dateRangeAllLoaded}
                        onLoadByCount={onLoadByCount}
                        onLoadByDateRange={onLoadByDateRange}
                        cancelDateRangeRef={cancelDateRangeRef}
                        txStartDate={txStartDate}
                        txEndDate={txEndDate}
                        setTxStartDate={setTxStartDate}
                        setTxEndDate={setTxEndDate}
                        enrichmentStatus={enrichmentStatus}
                    />
                </div>
            )}

            {/* ── Row 2: Search ── */}
            <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <input
                    type="text"
                    placeholder="Search counterparties..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="w-full pl-7 pr-2 py-1.5 text-xs bg-muted/50 border border-border rounded focus:outline-none focus:ring-1 focus:ring-primary"
                />
            </div>

            {/* ── Reset visibility + count ── */}
            {hiddenCanvasAddresses.size > 0 && (
                <div className="flex items-center justify-between px-2 py-1 text-[10px]">
                    <span className="text-muted-foreground">{hiddenCanvasAddresses.size} stashed</span>
                    <button
                        onClick={() => {
                            // v5.3: restore user-hide stashed items. Filter- and
                            // algorithm-stashed items are not touched — they're
                            // reinstated by relaxing the filter or algorithm.
                            canvasController.restoreByReason('user-hide');
                            canvasController.restoreByReason('manual-stash');
                        }}
                        className="px-2 py-0.5 text-[10px] font-medium text-blue-400 border border-blue-500/30 rounded hover:bg-blue-500/10 transition-colors"
                    >
                        Show All
                    </button>
                </div>
            )}

            {/* ── Column headers — also sort controls ── */}
            {/* Grid: [16px chevron][10px risk][1fr name][60px vol][28px tx#][28px eye] */}
            <div className="grid grid-cols-[16px_10px_1fr_60px_28px_28px] items-center gap-0.5 px-2 py-1 bg-muted/20 border border-border rounded text-[10px] text-muted-foreground">
                <div />
                <button
                    onClick={() => handleSort('risk')}
                    className={`flex items-center gap-0.5 uppercase tracking-wide font-semibold hover:text-foreground transition-colors ${sortField === 'risk' ? 'text-primary' : ''}`}
                    title="Sort by risk"
                >
                    <span className="w-2 h-2 rounded-full bg-current inline-block opacity-70" />
                    {sortField === 'risk' && <SortIcon className="w-2.5 h-2.5" />}
                </button>
                <button
                    onClick={() => handleSort('name')}
                    className={`flex items-center gap-0.5 uppercase tracking-wide font-semibold hover:text-foreground transition-colors text-left ${sortField === 'name' ? 'text-primary' : ''}`}
                >
                    Name {sortField === 'name' && <SortIcon className="w-2.5 h-2.5" />}
                </button>
                <button
                    onClick={() => handleSort('volume')}
                    className={`flex items-center gap-0.5 uppercase tracking-wide font-semibold hover:text-foreground transition-colors text-right justify-end ${sortField === 'volume' ? 'text-primary' : ''}`}
                >
                    Vol {sortField === 'volume' && <SortIcon className="w-2.5 h-2.5" />}
                </button>
                <button
                    onClick={() => handleSort('txCount')}
                    className={`flex items-center gap-0.5 uppercase tracking-wide font-semibold hover:text-foreground transition-colors text-center justify-center ${sortField === 'txCount' ? 'text-primary' : ''}`}
                >
                    Tx {sortField === 'txCount' && <SortIcon className="w-2.5 h-2.5" />}
                </button>
                <div className="flex items-center justify-center opacity-50" title="On canvas" aria-label="On canvas">
                    <Eye className="w-3 h-3" />
                </div>
            </div>

            {/* Count */}
            <div className="text-[11px] text-muted-foreground/60 px-1">
                {sorted.length} counterpart{sorted.length !== 1 ? 'ies' : 'y'}
            </div>

            {/* ── Counterparty rows ── */}
            <div className="flex flex-col gap-1">
                {sorted.map(group => (
                    <CounterpartyRow
                        key={group.key}
                        group={group}
                        isExpanded={expandedKey === group.key}
                        onToggleExpand={() => setExpandedKey(expandedKey === group.key ? null : group.key)}
                        onToggleCanvas={() => handleToggleCanvas(group)}
                        onAddToCanvas={() => {
                            const addr = group.addresses[0];
                            if (addr) onAddCounterpartyToCanvas?.(addr, group.displayName, group.entityType || undefined);
                        }}
                        onViewAllTxs={() => handleViewAllTxs(group)}
                        onCopyAddress={copyToClipboard}
                        selectedNodeId={selectedNode.id}
                        isHoverHighlighted={hoveredSource === 'canvas' && !!hoveredNodeId && group.addresses.some(a => a === hoveredNodeId)}
                        onMouseEnter={() => setHoveredNode(group.primaryAddress, 'inspector')}
                        onMouseLeave={() => setHoveredNode(null, 'inspector')}
                    />
                ))}
            </div>
        </div>
    );
};

// ── CounterpartyRow sub-component ────────────────────────────────────

interface CounterpartyRowProps {
    group: CounterpartyGroup;
    isExpanded: boolean;
    onToggleExpand: () => void;
    onToggleCanvas: () => void;
    onAddToCanvas: () => void;
    onViewAllTxs: () => void;
    onCopyAddress: (addr: string) => void;
    selectedNodeId: string;
    isHoverHighlighted?: boolean;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
}

const CounterpartyRow: React.FC<CounterpartyRowProps> = React.memo(({
    group,
    isExpanded,
    onToggleExpand,
    onToggleCanvas,
    onAddToCanvas: _onAddToCanvas,
    onViewAllTxs,
    onCopyAddress,
    selectedNodeId,
    isHoverHighlighted,
    onMouseEnter,
    onMouseLeave,
}) => {
    const isEntity = !group.displayName.includes('...');
    const dateRange = formatDateRange(group.transactions);

    // Inspector dust toggle — same pattern as TransfersTab. ON by default;
    // dust is filtered out before display. OFF: dust passes through and is
    // deprioritised at row level (opacity 0.45 + italic).
    const hideDust = useFilterStore(s => s.hideDust);

    // Sorted txs for expanded view (show all, most recent first).
    // Dust filter applies to the per-counterparty drilldown only; the
    // group totals stay as-is so the user always sees the true volume
    // even when dust rows are hidden.
    const sortedTxs = useMemo(() => {
        return [...group.transactions]
            .filter(t => !hideDust || !isHiddenDustRow(t))
            .sort((a, b) => {
                const ta = Number(a.timestamp) || 0;
                const tb = Number(b.timestamp) || 0;
                return tb - ta;
            });
    }, [group.transactions, hideDust]);

    // Collapse control for large tx lists
    const [showAllTxs, setShowAllTxs] = useState(false);
    const TX_PREVIEW_LIMIT = 10;
    const visibleTxs = showAllTxs ? sortedTxs : sortedTxs.slice(0, TX_PREVIEW_LIMIT);

    return (
        <div
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            className={`border rounded-md bg-card hover:bg-muted/30 transition-colors ${group.hasForensicLinks ? 'border-blue-500/50 bg-blue-500/5' : group.isOnTracePath ? 'border-amber-500/50 bg-amber-500/5' : 'border-border'} ${isHoverHighlighted ? 'ring-1 ring-blue-500 bg-blue-500/5' : ''}`}
        >
            {/* Forensic indicator */}
            {group.hasForensicLinks && (
                <div className="flex items-center gap-1 px-2.5 pt-1.5 text-[10px] text-blue-400 font-medium">
                    <ShieldCheck className="w-3 h-3" />
                    <span>Verified Forensic Links</span>
                </div>
            )}
            {/* Trace path indicator */}
            {group.isOnTracePath && (
                <div className="flex items-center gap-1 px-2.5 pt-1.5 text-[10px] text-amber-600 dark:text-amber-400 font-medium">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block" />
                    {group.traceContext ? (
                        <span>
                            {group.traceContext.direction === 'auto' ? 'Trace' : `${group.traceContext.direction.charAt(0).toUpperCase() + group.traceContext.direction.slice(1)} trace`} to {group.traceContext.targetName} (hop {group.traceContext.hop} of {group.traceContext.totalHops})
                        </span>
                    ) : (
                        <span>Trace Path</span>
                    )}
                </div>
            )}
            {/* ── Main row — grid matches column headers ── */}
            {/* Grid: [16px chevron][10px risk][1fr name][60px vol][28px tx#][28px eye] */}
            <div
                className="grid grid-cols-[16px_10px_1fr_60px_28px_28px] items-center gap-0.5 px-2 py-2 cursor-pointer"
                onClick={onToggleExpand}
            >
                {/* Expand chevron */}
                <div>
                    {isExpanded
                        ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                        : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
                    }
                </div>

                {/* Risk dot */}
                <div className={`w-2 h-2 rounded-full shrink-0 ${riskDotColor(group.entityType)}`} />

                {/* Name + entity type / address */}
                <div className="min-w-0">
                    <div className="flex items-center gap-1 min-w-0">
                        <EntityNameWithCopy
                            address={group.primaryAddress || group.addresses[0] || ''}
                            title={group.primaryAddress || group.addresses[0] || ''}
                            className={`text-[13px] ${isEntity ? 'font-medium' : 'font-mono text-muted-foreground'}`}
                            iconClassName="w-2.5 h-2.5"
                        >
                            {group.displayName}
                        </EntityNameWithCopy>
                        {group.addresses.length > 1 && (
                            <span className="text-[10px] text-muted-foreground/50 shrink-0">×{group.addresses.length}</span>
                        )}
                    </div>
                    <div className="flex items-center gap-1 text-[10px] text-muted-foreground mt-px">
                        {group.entityType && (
                            <span className="px-1 py-px bg-muted rounded shrink-0">{group.entityType}</span>
                        )}
                        {isEntity && (
                            <span className="font-mono truncate opacity-60">{truncateAddr(group.addresses[0])}</span>
                        )}
                        {dateRange && <span className="shrink-0 opacity-60">{dateRange}</span>}
                    </div>
                </div>

                {/* Volume — colored by net flow direction. formatCurrency respects
                    the inspector-header ORIG/USD toggle and embeds the primary token
                    so this row stays consistent with Transfers. compact:true keeps
                    the K/M abbreviation appropriate for an aggregate row. */}
                <div className="text-right min-w-0">
                    <div className={`text-[13px] font-semibold tabular-nums truncate ${
                        group.inboundVolume > group.outboundVolume ? 'text-green-600 dark:text-green-400' :
                        group.outboundVolume > group.inboundVolume ? 'text-red-600 dark:text-red-400' :
                        'text-foreground'
                    }`}>
                        {formatCurrency(group.totalVolume, group.primaryToken && group.primaryToken !== 'native' ? group.primaryToken : undefined, { compact: true })}
                    </div>
                </div>

                {/* Tx count */}
                <div className="text-[11px] tabular-nums text-center text-muted-foreground">
                    {group.txCount}
                </div>

                {/* Eye toggle: 3 states */}
                <div className="flex justify-center" onClick={e => e.stopPropagation()}>
                    <button
                        onClick={onToggleCanvas}
                        className="p-1 rounded hover:bg-muted transition-colors"
                        title={
                            group.isOnCanvas && !group.isHiddenOnCanvas ? 'Hide from canvas' :
                            group.isOnCanvas && group.isHiddenOnCanvas ? 'Show on canvas' :
                            'Add to canvas'
                        }
                    >
                        {group.isOnCanvas && !group.isHiddenOnCanvas
                            ? <Eye className="w-3.5 h-3.5 text-primary" />
                            : group.isOnCanvas && group.isHiddenOnCanvas
                                ? <EyeOff className="w-3.5 h-3.5 text-foreground" />
                                : <EyeOff className="w-3.5 h-3.5 text-muted-foreground/40" />
                        }
                    </button>
                </div>
            </div>

            {/* ── Expanded: Transaction list ── */}
            {isExpanded && (
                <div className="border-t border-border px-3 py-2 bg-muted/20">
                    <div className="space-y-0">
                        {visibleTxs.map(tx => {
                            const isInbound = tx.direction === 'incoming' ||
                                (tx.to_address === selectedNodeId);
                            const fromAddr = tx.from_address || '';
                            const toAddr = tx.to_address || '';
                            const fromLabel = safeStr(tx.from_entity_name) || truncateAddr(fromAddr);
                            const toLabel = safeStr(tx.to_entity_name) || truncateAddr(toAddr);
                            const isForensic = (tx as any).forensic_context?.verified;
                            // Safe timestamp parsing (epoch + ISO 8601)
                            const parsedTs = (() => {
                                if (!tx.timestamp) return null;
                                const n = Number(tx.timestamp);
                                if (!isNaN(n)) return new Date(n > 1e12 ? n : n * 1000);
                                const d = new Date(String(tx.timestamp));
                                return isNaN(d.getTime()) ? null : d;
                            })();

                            // Dust rows only appear here when hideDust is OFF
                            // (otherwise they're filtered above). Render them at half
                            // opacity + italic so they don't compete with material rows.
                            const isDust = isHiddenDustRow(tx);
                            return (
                                <div
                                    key={tx.tx_hash}
                                    title={isDust ? `Dust transaction (≈$0.00, below \$${DUST_USD_THRESHOLD} threshold)` : undefined}
                                    className={`px-2 py-1.5 border-l-[3px] ${isForensic ? 'border-l-blue-500 shadow-[inset_0_0_10px_rgba(59,130,246,0.05)] bg-blue-500/5 hover:bg-blue-500/10' : isInbound ? 'border-l-green-500 hover:bg-muted/30' : 'border-l-red-500 hover:bg-muted/30'} border-b border-border/30 last:border-b-0 transition-colors ${isDust ? 'opacity-45 italic hover:opacity-90' : ''}`}
                                >
                                    <div className="grid grid-cols-[16px_44px_minmax(0,1fr)_92px_32px_84px_16px] items-center gap-2">
                                        {isInbound
                                            ? <ArrowDownLeft className={`w-4 h-4 shrink-0 ${isForensic ? 'text-blue-400' : 'text-green-500'}`} />
                                            : <ArrowUpRight className={`w-4 h-4 shrink-0 ${isForensic ? 'text-blue-400' : 'text-red-500'}`} />
                                        }
                                        <span className="shrink-0 text-[10px] text-muted-foreground tabular-nums w-[44px]">
                                            {parsedTs ? parsedTs.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '—'}
                                        </span>
                                        <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-1 min-w-0 text-[13px]">
                                            <span className={`truncate ${isForensic ? 'text-blue-400 font-bold' : safeStr(tx.from_entity_name) ? 'text-blue-600 dark:text-blue-400 font-medium' : 'font-mono text-foreground/80'}`} title={fromAddr}>
                                                {fromLabel}
                                            </span>
                                            <span className="text-muted-foreground/40">→</span>
                                            <span className={`truncate ${isForensic ? 'text-blue-400 font-bold' : safeStr(tx.to_entity_name) ? 'text-blue-600 dark:text-blue-400 font-medium' : 'font-mono text-foreground/80'}`} title={toAddr}>
                                                {toLabel}
                                            </span>
                                        </div>
                                        <span className={`shrink-0 text-[13px] font-semibold tabular-nums text-right ${isForensic ? 'text-blue-400' : isInbound ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                            {/* No token here — the token symbol renders separately in the next column.
                                                formatCurrency w/o token still respects the ORIG/USD toggle. */}
                                            {formatCurrency(Number(tx.value) || 0)}
                                        </span>
                                        <span className="shrink-0 text-[10px] text-muted-foreground/60 w-[32px]">{safeStr(tx.token_symbol) || ''}</span>
                                        <div className="flex items-center gap-1 shrink-0 w-[84px] overflow-hidden">
                                            {isForensic && <ShieldCheck className="w-3 h-3 text-blue-400 shrink-0" />}
                                            <span className="text-[10px] font-mono text-muted-foreground/70 truncate" title={tx.tx_hash || ''}>
                                                {tx.tx_hash ? truncateAddr(tx.tx_hash) : '—'}
                                            </span>
                                        </div>
                                        {tx.tx_hash && (
                                            <button
                                                onClick={() => onCopyAddress(tx.tx_hash)}
                                                className="p-0.5 hover:bg-muted rounded shrink-0"
                                                title="Copy tx hash"
                                            >
                                                <Copy className="w-3 h-3 text-muted-foreground" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {sortedTxs.length > TX_PREVIEW_LIMIT && (
                        <button
                            onClick={() => setShowAllTxs(prev => !prev)}
                            className="mt-2 text-[11px] text-primary hover:underline"
                        >
                            {showAllTxs ? 'Show fewer' : `Show all ${sortedTxs.length} transactions`}
                        </button>
                    )}

                    {/* Multi-address list for entity groups */}
                    {group.addresses.length > 1 && (
                        <div className="mt-2 pt-2 border-t border-border/50">
                            <div className="text-[10px] text-muted-foreground/60 mb-1">Addresses in this group:</div>
                            {group.addresses.map(addr => (
                                <div key={addr} className="flex items-center gap-1.5 text-[11px] py-0.5">
                                    <span className="font-mono text-muted-foreground truncate">{truncateAddr(addr)}</span>
                                    <button
                                        onClick={() => onCopyAddress(addr)}
                                        className="p-0.5 hover:bg-muted rounded shrink-0"
                                        title="Copy address"
                                    >
                                        <Copy className="w-2.5 h-2.5" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
});

CounterpartyRow.displayName = 'CounterpartyRow';
